/**
 * Group Mentions Utility Module
 * Handles group mentions (@everyone, @admins, @managers, @providers, @clients) in tasks and replies
 */

export const GROUP_MENTION_PREFIX = "group:";

// Role IDs matching backend
const ROLE_ADMIN = 1;
const ROLE_PROVIDER = 2;
const ROLE_PATIENT = 3;
const ROLE_MANAGER = 5;

/**
 * Group definitions with display information
 */
export const GROUP_DEFINITIONS = {
  everyone: {
    id: "group:everyone",
    marker: "@everyone",
    roleIds: [ROLE_ADMIN, ROLE_MANAGER, ROLE_PROVIDER, ROLE_PATIENT],
    description: "All admins, managers, providers, and clients",
    icon: "fe-users",
    color: "#8b7cc9",
  },
  admins: {
    id: "group:admins",
    marker: "@admins",
    roleIds: [ROLE_ADMIN],
    description: "Super Admins",
    icon: "fe-shield",
    color: "#c06060",
  },
  managers: {
    id: "group:managers",
    marker: "@managers",
    roleIds: [ROLE_MANAGER],
    description: "Managers",
    icon: "fe-briefcase",
    color: "#6aab88",
  },
  providers: {
    id: "group:providers",
    marker: "@providers",
    roleIds: [ROLE_PROVIDER],
    description: "Providers",
    icon: "fe-user-check",
    color: "#6a9fc8",
  },
  clients: {
    id: "group:clients",
    marker: "@clients",
    roleIds: [ROLE_PATIENT],
    description: "Clients",
    icon: "fe-heart",
    color: "#d4a054",
  },
};

/**
 * Check if a mention ID is a group mention
 * @param {string} mentionId - The mention ID to check
 * @returns {boolean}
 */
export function isGroupMention(mentionId) {
  return (
    typeof mentionId === "string" && mentionId.startsWith(GROUP_MENTION_PREFIX)
  );
}

/**
 * Check if a user can use group mentions based on their role
 * @param {Object} user - The user object with isAdmin/isManagingAdmin properties
 * @returns {boolean}
 */
export function canUseGroupMentions(user) {
  return user && (user.isAdmin === true || user.isManagingAdmin === true);
}

/**
 * Get users matching specific role IDs from usersWithRoles
 * @param {Array} usersWithRoles - Array of users with roleId field
 * @param {Array<number>} roleIds - Role IDs to filter by
 * @returns {Array}
 */
export function getGroupUsers(usersWithRoles, roleIds) {
  return usersWithRoles.filter((user) => roleIds.includes(user.roleId));
}

/**
 * Count users in each group
 * @param {Array} usersWithRoles - Array of users with roleId field
 * @returns {Object} Object with group key and user count
 */
export function getGroupCounts(usersWithRoles) {
  const counts = {};
  for (const [key, group] of Object.entries(GROUP_DEFINITIONS)) {
    counts[key] = getGroupUsers(usersWithRoles, group.roleIds).length;
  }
  return counts;
}

/**
 * Build group mention items for CKEditor feed
 * @param {Array} usersWithRoles - Array of users with roleId field
 * @param {Object} [dynamicLabels] - Optional dynamic labels keyed by group key (e.g. { providers: "Therapists" })
 * @returns {Array} Array of mention items for CKEditor
 */
export function buildGroupMentionItems(usersWithRoles, dynamicLabels = {}) {
  const counts = getGroupCounts(usersWithRoles);
  const items = [];

  for (const [key, group] of Object.entries(GROUP_DEFINITIONS)) {
    const count = counts[key];
    const label = dynamicLabels[key];
    const marker = label ? `@${label.toLowerCase()}` : group.marker;
    let description = label || group.description;
    if (key === "everyone") {
      const parts = ["admins", "managers"];
      parts.push(
        dynamicLabels.providers
          ? dynamicLabels.providers.toLowerCase()
          : "providers"
      );
      parts.push(
        dynamicLabels.clients ? dynamicLabels.clients.toLowerCase() : "clients"
      );
      const last = parts.pop();
      description = `All ${parts.join(", ")}, and ${last}`;
    }
    items.push({
      id: group.id,
      marker: marker,
      name: `${marker} (${count} users)`,
      username: marker.substring(1),
      description: description,
      icon: group.icon,
      color: group.color,
      isGroup: true,
      count: count,
    });
  }

  return items;
}

/**
 * Extract tagged user IDs from description HTML
 * @param {string} description - HTML description with mention tags
 * @returns {Array<string>} Array of user IDs and group mention IDs
 */
export function extractTaggedUsers(description) {
  if (!description) return [];

  const regex = /data-mention-id="([^"]+)"/g;
  const ids = [];
  let match;

  while ((match = regex.exec(description)) !== null) {
    ids.push(match[1]);
  }

  return ids;
}

/**
 * Check if tagged users array contains any group mentions
 * @param {Array<string>} taggedUsers - Array of user/group IDs
 * @returns {boolean}
 */
export function hasGroupMentions(taggedUsers) {
  return taggedUsers.some((id) => isGroupMention(id));
}

/**
 * Get details about group mentions for confirmation dialog
 * @param {Array<string>} taggedUsers - Array of user/group IDs
 * @param {Array} usersWithRoles - Array of users with roleId field
 * @param {Object} [dynamicLabels] - Optional dynamic labels keyed by group key (e.g. { providers: "Therapists" })
 * @returns {Object} Object with groups array and totalUsers count
 */
export function getGroupMentionDetails(
  taggedUsers,
  usersWithRoles,
  dynamicLabels = {}
) {
  const groupMentions = taggedUsers.filter((id) => isGroupMention(id));
  const individualMentions = taggedUsers.filter((id) => !isGroupMention(id));

  const groups = [];
  const seenUserIds = new Set();

  individualMentions.forEach((id) => seenUserIds.add(id));

  for (const groupId of groupMentions) {
    const groupKey = groupId.replace(GROUP_MENTION_PREFIX, "");
    const group = GROUP_DEFINITIONS[groupKey];

    if (group) {
      const groupUsers = getGroupUsers(usersWithRoles, group.roleIds);
      let newUsers = 0;

      groupUsers.forEach((user) => {
        if (!seenUserIds.has(String(user.user_id))) {
          seenUserIds.add(String(user.user_id));
          newUsers++;
        }
      });

      const label = dynamicLabels[groupKey];
      const marker = label ? `@${label.toLowerCase()}` : group.marker;
      groups.push({
        id: groupId,
        name: marker,
        description: label || group.description,
        count: newUsers,
        totalInGroup: groupUsers.length,
      });
    }
  }

  return {
    groups,
    individualCount: individualMentions.length,
    totalUsers: seenUserIds.size,
  };
}
